import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import DemoButton from '../Common/DemoButton';
import './AboutPage.css';

// Story illustrations (from Figma design)
import story2000sImg from '../../assets/story/story-2000s.png';
import storyDigitalImg from '../../assets/story/story-digital-1.png';
import storyPatternImg from '../../assets/story/story-pattern-1.svg';
import storyAiImg from '../../assets/story/story-ai.svg';
import storyObsImg from '../../assets/story/story-observation.png';
import storyBillImg from '../../assets/story/story-bill.svg';
import storyTodayImg from '../../assets/story/story-today.svg';

// Add headshots to src/assets/team/ then replace the null values:
// import KaranPhoto   from '../../assets/team/karan-shah.jpg';
// import PrasannPhoto from '../../assets/team/prasann-shah.jpg';
const KaranPhoto = null;
const PrasannPhoto = null;

const STATS = [
  { value: '6,000+', label: 'Active users' },
  { value: '180+', label: 'Companies' },
  { value: '2015', label: 'Founded' },
  { value: '38+', label: 'Team members' },
];

const FOUNDERS = [
  {
    name: 'Karan Shah',
    role: 'Co-Founder & CEO',
    photo: KaranPhoto,
    bio: "Karan spent years in corporate strategy at Adani Logistics, one of India's largest conglomerates, watching freight forwarders run complex multi-party operations across email threads, WhatsApp groups, and paper trails. A Stanford GSB alumnus and BTech in Computer Engineering from Nirma University, he co-founded Shipmnts in 2015 to give forwarders the platform they deserved.",
    linkedin: 'https://in.linkedin.com/in/karannareshshah',
  },
  {
    name: 'Prasann Shah',
    role: 'Co-Founder & CTO',
    photo: PrasannPhoto,
    bio: "Prasann brings deep engineering experience from Flipkart, India's logistics-intensive e-commerce pioneer, where he worked as a Senior Software Engineer. A BTech in Information Technology from DA-IICT Ahmedabad, he architects the technical foundation that lets 6,000+ users manage freight operations with confidence every day.",
    linkedin: 'https://www.linkedin.com/in/preachx',
  },
];

const schemaOrg = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  url: 'https://shipmnts.com/about',
  name: 'About Shipmnts',
  description:
    'Shipmnts is a freight ERP platform built by Karan Shah and Prasann Shah in 2015. Trusted by 6,000+ users across 180 freight forwarding companies in India.',
  publisher: { '@id': 'https://shipmnts.com/#organization' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://shipmnts.com' },
      { '@type': 'ListItem', position: 2, name: 'About', item: 'https://shipmnts.com/about' },
    ],
  },
};

/* Image-based illustration panel */
function NodeIllus({ img, tint, blend }) {
  return (
    <div className="os-node-illus">
      <img src={img} alt="" />
      {tint && (
        <div
          className="os-node-illus-tint"
          style={{ background: tint, mixBlendMode: blend || 'color-burn' }}
        />
      )}
    </div>
  );
}

/* Gradient illustration panel (used for founders card) */
function NodeIllusGradient({ children }) {
  return <div className="os-node-illus os-node-illus--gradient">{children}</div>;
}

export default function AboutPage() {
  return (
    <div className="about-page">
      <Helmet>
        <title>Our Story: Shipmnts Freight ERP Built for India</title>
        <meta
          name="description"
          content="Shipmnts is a freight ERP platform founded in 2015 by Karan Shah and Prasann Shah. Trusted by 6,000+ users across 180 freight forwarding companies in India."
        />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      </Helmet>

      {/* ── Main story section ───────────────────────────────────────────────── */}
      <section className="os-story">
        {/* Title */}
        <h1 className="os-main-title">
          A Story of Chaos in a <em>Trillion Dollar Industry</em>
        </h1>

        {/* Intro */}
        <div className="os-intro-text">
          <p>
            Hi there! If you're reading this, chances are your world looks a bit like this →{' '}
            <strong>
              Multiple Spreadsheets open, inbox overloaded, three calls deep just to track a single
              AWB/ HBL
            </strong>
          </p>
          <p>&nbsp;</p>
          <p>
            You probably <strong>juggle</strong> a dozen systems...
            <br />
            One for tracking, one for invoicing, one for quoting, and one for... just remembering
            where the file was saved.
            <br />
            <br />
            We began our journey peeking into a world that kept the global economy worth $16
            Trillion moving... Yet ran on disconnected tools and endless manual effort.
          </p>
          <p>&nbsp;</p>
          <p>
            We asked ourselves:
            <br />
            <strong>
              Why does freight forwarding still run on Excel &amp; paperwork in 2025? Why does this
              still feel like the 90s in a world of AI and APIs?
            </strong>
          </p>
          <p>&nbsp;</p>
          <p>To answer that, we have to go back a bit.</p>
        </div>

        {/* Story nodes */}
        <div className="os-nodes">
          {/* Node 1: 2000s: composited screenshot, blend already baked in */}
          <div className="os-node">
            <NodeIllus img={story2000sImg} />
            <div className="os-node-content">
              <h2 className="os-node-heading">2000s</h2>
              <div className="os-node-body">
                <p>
                  The rise of freight tech (on paper)
                  <br />
                  As the global trade engine grew louder, most freight offices grew messier.
                  Invoices were typed manually. Charges scribbled on expense pads.
                  <br />
                  Freight still moved fast, but the back office? Not so much.
                </p>
                <p className="os-italic">
                  We shadowed those offices. The ones with dot matrix printers, stamp pads, and
                  folders thicker than the shipments themselves.
                </p>
              </div>
            </div>
          </div>

          {/* Node 2: Digital: composited screenshot, blend already baked in */}
          <div className="os-node">
            <NodeIllus img={storyDigitalImg} />
            <div className="os-node-content">
              <h2 className="os-node-heading">{`Then came "digital"... well kind of digital`}</h2>
              <div className="os-node-body">
                <p>
                  Excel was a miracle. So was email.
                  <br />
                  But soon we had 15 tools doing the job of 1.
                </p>
                <p>
                  Quoting happened on WhatsApp.
                  <br />
                  Collections on call.
                  <br />
                  Filing and follow-ups in folders.
                </p>
                <p>The chaos wasn't just inefficient, it was invisible.</p>
              </div>
            </div>
          </div>

          {/* Node 3: Pattern */}
          <div className="os-node">
            <NodeIllus img={storyPatternImg} />
            <div className="os-node-content">
              <h2 className="os-node-heading">We saw the pattern.</h2>
              <div className="os-node-body">
                <p>Too many tools. Too many tabs. Too little time.</p>
                <p>
                  Quotes on email. Bookings on WhatsApp.
                  <br />
                  Billing on Excel. Status updates on calls.
                </p>
                <p>
                  <strong>Deadlines were being missed. Revenue was leaking.</strong>
                  <br />
                  Missed charges, forgotten follow-ups, lost documents: it all added up. Not
                  because people didn't care, but because no one knew where the latest update lived.
                </p>
                <p>
                  We realized these weren't just inefficiencies.
                  <br />
                  They were invisible time drains.
                  <br />
                  And no one was solving for the people behind the scenes: the teams making trade
                  happen yet the economy kept booming and demands came rolling in!
                  <br />
                  <br />
                  <strong>So we rolled up our sleeves and started building....</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Founders: integrated as a story card */}
          <div className="os-node os-node--founders">
            <NodeIllusGradient>
              <span className="os-founders-illus-label">The Team</span>
              <span className="os-founders-illus-sub">Two insiders who lived the chaos</span>
            </NodeIllusGradient>
            <div className="os-node-content">
              <h2 className="os-node-heading">Two people who'd seen it from the inside</h2>
              <div className="os-node-body os-node-body--founders">
                <p>
                  This wasn't a problem we stumbled onto. We lived it: from inside India's largest
                  logistics and e-commerce operations.
                </p>
                <div className="os-founders-grid">
                  {FOUNDERS.map(({ name, role, photo, bio, linkedin }) => (
                    <div className="os-founder-card" key={name}>
                      <div className="os-founder-photo-wrap">
                        {photo ? (
                          <img src={photo} alt={name} className="os-founder-photo" />
                        ) : (
                          <div className="os-founder-photo-placeholder">
                            {name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')}
                          </div>
                        )}
                      </div>
                      <div className="os-founder-info">
                        <p className="os-founder-name">{name}</p>
                        <p className="os-founder-role">{role}</p>
                        <p className="os-founder-bio">{bio}</p>
                        <a
                          href={linkedin}
                          target="_blank"
                          rel="noreferrer"
                          className="os-founder-linkedin"
                        >
                          LinkedIn →
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Node 4: AI */}
          <div className="os-node">
            <NodeIllus img={storyAiImg} tint="#bc4429" blend="color-burn" />
            <div className="os-node-content">
              <h2 className="os-node-heading">We started with AI, before the market was ready.</h2>
              <div className="os-node-body">
                <p>
                  Our original vision was bold: use machine learning to automate ops, capture missed
                  charges, and predict delays.
                </p>
                <p>&nbsp;</p>
                <p>
                  But in an industry still running on Excel, AI felt too early.
                  <br />
                  Teams loved the idea - but weren't ready to adopt or pay for it.
                </p>
                <p>&nbsp;</p>
                <p>
                  So we shifted focus.
                  <br />
                  We built simple, intuitive tools that solved everyday chaos - while quietly laying
                  the groundwork for intelligent workflows.
                </p>
                <p>&nbsp;</p>
                <p>
                  Now, as AI becomes mainstream, that early start sets us apart. At Shipmnts, AI
                  isn't an afterthought. It's in our DNA - and now it powers everything from charge
                  detection to smart billing.
                </p>
                <p>&nbsp;</p>
                <p>Full circle. Better timed.</p>
              </div>
            </div>
          </div>

          {/* Node 5: Observation */}
          <div className="os-node">
            <NodeIllus img={storyObsImg} tint="#722919" blend="plus-lighter" />
            <div className="os-node-content">
              <h2 className="os-node-heading">
                Shipmnts was born from deep observation, not assumption.
              </h2>
              <div className="os-node-body">
                <p>That shift led to Shipmnts.</p>
                <p>&nbsp;</p>
                <p>
                  We sat with freight teams. <br />
                  Not to replace people. But to free them from the madness of Ctrl+F and Ctrl+C.
                  <br />
                  Watched them toggle between screens.
                  <br />
                  Copied how charges were calculated.
                  <br />
                  Saw how a "simple" invoice could take hours, and how critical tasks lived in memory
                  or makeshift task sheets.
                </p>
                <p>&nbsp;</p>
                <p>
                  So we built one simplified, collaborative platform.
                  <br />
                  To reduce the tabs, remove the back-and-forth, and give modern freight teams
                  clarity, not chaos.
                </p>
              </div>
            </div>
          </div>

          {/* Node 6: The question */}
          <div className="os-node">
            <NodeIllus img={storyBillImg} tint="#bc4429" blend="color-burn" />
            <div className="os-node-content">
              <h2 className="os-node-heading">
                What if freight teams didn't need five tools and six follow-ups just to raise a
                bill?
              </h2>
              <div className="os-node-body">
                <p>
                  What if every shipment could be quoted, tracked, documented, and invoiced
                  <br />
                  in one place, with zero guesswork?
                </p>
                <p>&nbsp;</p>
                <p>That's exactly what Shipmnts.com is built for.</p>
                <p>&nbsp;</p>
                <p>
                  <strong>
                    From automated charge capture and real-time shipment updates, to smart document
                    generation and one-click invoicing -{' '}
                  </strong>
                  we bring your freight workflow into a single, modern platform.
                </p>
                <p>
                  No more chasing emails.
                  <br />
                  No more missed line items.
                  <br />
                  No more confusion over "who did what when."
                </p>
                <p>&nbsp;</p>
                <p>
                  Just faster sales. simplified ops. Clear billing.
                  <br />
                  And more time to actually grow your business.
                </p>
              </div>
            </div>
          </div>

          {/* Node 7: Today */}
          <div className="os-node">
            <NodeIllus img={storyTodayImg} tint="#6f2a1b" blend="screen" />
            <div className="os-node-content">
              <h2 className="os-node-heading">Today, the best run on Shipmnts.com</h2>
              <div className="os-node-body">
                <p>
                  That's where <strong>Shipmnts</strong> comes in. We set out to rethink how freight
                  teams work - Not by adding more tools, but by replacing the clutter with one
                  smart, connected platform.
                </p>
                <p>&nbsp;</p>
                <p>
                  Instead of juggling Excel files, emails, WhatsApp threads, and siloed ERPs, teams
                  can now manage their{' '}
                  <strong>entire freight operation in one collaborative workspace.</strong>
                </p>
                <p>&nbsp;</p>
                <p>
                  Quoting, booking, documentation, tracking, invoicing - even customer updates - all
                  happen in real time, in one place,{' '}
                  <strong>with every stakeholder on the same page.</strong>
                </p>
                <p>
                  <br />
                  And because no two freight companies work exactly the same, Shipmnts is{' '}
                  <strong>fully configurable.</strong>
                  <br />
                  Custom charge structures, workflow templates, job formats, team permissions - you
                  shape the platform around your business, not the other way around.
                </p>
                <p>
                  <br />
                  Whether you're a 5-person team or a growing global forwarder, Shipmnts helps you
                  build the back office of the future -{' '}
                  <strong>
                    collaborative, customizable, and built for the real pace of logistics.
                  </strong>
                </p>
                <p>&nbsp;</p>
                <p className="os-italic">
                  Work your way.
                  <br />
                  With tools that finally work for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────────────── */}
      <section className="os-stats-section">
        <div className="os-stats-inner">
          <div className="os-stats-grid">
            {STATS.map(({ value, label }) => (
              <div className="os-stat-card" key={label}>
                <span className="os-stat-value">{value}</span>
                <span className="os-stat-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────────── */}
      <section className="os-cta-section">
        <div className="os-cta-inner">
          <h2 className="os-cta-title">See Shipmnts in action</h2>
          <p className="os-cta-sub">
            30 minutes. No slides. Just a live walkthrough of your actual workflows.
          </p>
          <div className="os-cta-actions">
            <DemoButton label="Book a Demo" size="large" />
            <Link to="/#products" className="os-cta-link">
              Explore the platform →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
